import { USER_COLUMN_FILTER_KEYS, USER_EXPORT_COLUMNS } from '../constants/constants';
import { exportRowsToExcel } from '../../../utils/excelExport';
import { mapAuditFields, mapAuditExportFields } from '../../../lib/auditColumnDefs';
import type { RoleOption } from '../../roles/models/types';
import type {
  MerchantOption,
  PlatformUserDto,
  PlatformUserListRequest,
  PlatformUserListResponse,
  PlatformUserRow,
  UserColumnFilterKey,
  UserColumnFilters,
} from '../models/types';

const ROLE_BADGE_STYLES: { test: (key: string) => boolean; bg: string; color: string }[] = [
  { test: key => key.includes('SUPER'), bg: '#fee2e2', color: '#dc2626' },
  { test: key => key.includes('ADMIN'), bg: '#ede9fe', color: '#7c3aed' },
  { test: key => key.includes('MERCHANT') || key === 'SUB_USER', bg: '#dbeafe', color: '#2563eb' },
  { test: key => key.includes('ANALYST') || key.includes('FINANCE') || key.includes('COMPLIANCE'), bg: '#cffafe', color: '#0e7490' },
  { test: key => key.includes('SUPPORT'), bg: '#d1fae5', color: '#059669' },
];

const DEFAULT_ROLE_STYLE = { bg: '#f1f5f9', color: '#64748b' };

export function toRoleSelectOptions(roles: RoleOption[]) {
  return roles.map((role) => ({
    value: String(role.autoId),
    label: `${role.roleName} (${role.roleCode})`,
    searchText: `${role.roleName} ${role.roleCode}`,
  }));
}

export function normalizeRoles(raw: unknown): RoleOption[] {
  const list = Array.isArray(raw) ? raw : [];
  return list
    .map((item: Record<string, unknown>) => ({
      autoId: Number(item.autoId ?? item.id ?? 0),
      roleCode: String(item.roleCode ?? '').trim(),
      roleName: String(item.roleName ?? item.roleCode ?? '').trim(),
      roleScope: String(item.roleScope ?? 'ADMIN').toUpperCase() as RoleOption['roleScope'],
      status: item.status != null ? String(item.status) : undefined,
    }))
    .filter((role) => role.autoId > 0 && role.roleName.length > 0);
}

export function roleStyle(roleName: string) {
  const key = roleName.trim().toUpperCase();
  return ROLE_BADGE_STYLES.find(entry => entry.test(key)) ?? DEFAULT_ROLE_STYLE;
}

export function normalizeMerchants(raw: unknown): MerchantOption[] {
  const body = raw as Record<string, unknown> | null;
  const list = Array.isArray(body?.content)
    ? body.content
    : Array.isArray(body?.data)
      ? body.data
      : Array.isArray(body)
        ? body
        : Array.isArray((body?.data as Record<string, unknown>)?.content)
          ? (body?.data as Record<string, unknown>).content
          : [];

  return (list as Record<string, unknown>[])
    .map((item) => ({
      autoId: Number(item.autoId ?? item.id ?? 0),
      name: String(item.name ?? item.merchantName ?? item.companyName ?? '').trim(),
      email: item.email != null ? String(item.email) : undefined,
      status: item.status != null ? String(item.status) : undefined,
    }))
    .filter((m) => m.autoId > 0 && m.name.length > 0);
}

export function resolveMerchantIdForCompany(
  company: string,
  merchants: MerchantOption[],
): string {
  const normalizedCompany = company.trim().toLowerCase();
  if (!normalizedCompany || normalizedCompany === '—') return '';
  const match = merchants.find(
    (merchant) => merchant.name.trim().toLowerCase() === normalizedCompany,
  );
  return match ? String(match.autoId) : '';
}

export function isMerchantScopedRole(role: RoleOption | null): boolean {
  if (!role) return false;
  const code = role.roleCode.toUpperCase();
  return role.roleScope === 'MERCHANT' || code === 'MERCHANT' || code === 'SUB_USER';
}

/** Mirrors isMerchantScopedRole above — gates the "Referral user" toggle to exactly the case
 * that's mutually exclusive with needing a single Merchant selection. */
export function isAdminScopedRole(role: RoleOption | null): boolean {
  if (!role) return false;
  return role.roleScope === 'ADMIN';
}

export function getDefaultMerchantRoleId(roles: RoleOption[]): number {
  const merchantRole = roles.find((role) => role.roleCode.toUpperCase() === 'MERCHANT')
    ?? roles.find((role) => role.roleScope === 'MERCHANT');
  return merchantRole?.autoId ?? roles[0]?.autoId ?? 0;
}

function formatDisplayStatus(status?: string): string {
  if (!status) return 'Inactive';
  const normalized = status.trim().toLowerCase();
  return normalized.charAt(0).toUpperCase() + normalized.slice(1);
}

export function formatDateTime(value?: string | null): string {
  if (!value) return 'Never';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Never';
  return date.toLocaleString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function formatJoinedDate(value?: string | null): string {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString('en-GB', { month: 'short', year: 'numeric' });
}

export function formatRelativeTime(value?: string | null): string {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';

  const diffMs = Date.now() - date.getTime();
  if (diffMs < 0) return 'just now';

  const diffSeconds = Math.floor(diffMs / 1000);
  if (diffSeconds < 60) return 'just now';

  const diffMinutes = Math.floor(diffSeconds / 60);
  if (diffMinutes < 60) return `${diffMinutes} min ago`;

  const diffHours = Math.floor(diffMinutes / 60);
  if (diffHours < 24) return `${diffHours} h ago`;

  const diffDays = Math.floor(diffHours / 24);
  if (diffDays < 30) return `${diffDays} d ago`;

  const diffMonths = Math.floor(diffDays / 30);
  if (diffMonths < 12) return `${diffMonths} mo ago`;

  const diffYears = Math.floor(diffMonths / 12);
  return `${diffYears} y ago`;
}

export function mapPlatformUserDto(dto: PlatformUserDto): PlatformUserRow | null {
  const id = Number(dto.userId ?? 0);
  const name = String(dto.name ?? '').trim();
  const email = String(dto.email ?? '').trim();
  if (!id || !name || !email) return null;

  return {
    id,
    userType: String(dto.userType ?? '').toUpperCase(),
    roleId: dto.roleId != null ? Number(dto.roleId) : undefined,
    name,
    email,
    role: String(dto.role ?? 'User').trim(),
    status: formatDisplayStatus(dto.status),
    lastLogin: formatDateTime(dto.lastLoginTime),
    lastLoginRelative: formatRelativeTime(dto.lastLoginTime),
    joined: formatJoinedDate(dto.createdDate ?? dto.joinedDate),
    department: String(dto.department ?? '—').trim() || '—',
    company: String(dto.companyName ?? '—').trim() || '—',
    merchantName:
      String(dto.merchantName ?? dto.companyName ?? '—').trim() || '—',
    sandboxModeEnabled: dto.sandboxModeEnabled ?? null,
    checkoutLinkEmailEnabled: dto.checkoutLinkEmailEnabled ?? null,
    liveDccEnabled: dto.liveDccEnabled ?? null,
    assignedMerchantIds: Array.isArray(dto.assignedMerchantIds) ? dto.assignedMerchantIds : undefined,
    assignedMerchantNames: Array.isArray(dto.assignedMerchantNames) ? dto.assignedMerchantNames : undefined,
    twoFactorEnabled: dto.twoFactorEnabled ?? true,
    dashboardKey: dto.dashboardKey != null ? String(dto.dashboardKey) : undefined,
    ...mapAuditFields(dto as unknown as Record<string, unknown>),
  };
}

export function normalizePlatformUsers(raw: unknown): PlatformUserRow[] {
  const data = (raw ?? {}) as PlatformUserListResponse;

  const source = Array.isArray(data.content) && data.content.length > 0
    ? data.content
    : [
        ...(Array.isArray(data.admins) ? data.admins : []),
        ...(Array.isArray(data.merchants) ? data.merchants : []),
      ];

  return source
    .map(mapPlatformUserDto)
    .filter((user): user is PlatformUserRow => user != null);
}

export function userRowKey(user: PlatformUserRow): string {
  return `${user.userType}-${user.id}`;
}

export function toApiStatus(displayStatus: string): string {
  return displayStatus.trim().toUpperCase();
}

export function isSuperAdminRow(user: PlatformUserRow): boolean {
  const role = user.role.trim().toLowerCase();
  return user.userType === 'ADMIN' && (role.includes('super') || role === 'super admin');
}

const USER_COLUMN_FILTER_KEY_SET: Record<UserColumnFilterKey, true> = {
  name: true,
  email: true,
  role: true,
  status: true,
  lastLogin: true,
};

/**
 * Translate AG Grid's native column filterModel into the same shape the Filters drawer
 * stores in `columnFilters` / feeds `buildPlatformUserRequest`.
 *
 * Text filters → `{ [colId]: [filterText] }`.
 */
export function mapAgGridFilterModelToUserFilters(
  filterModel: Record<string, unknown> | null | undefined,
): Partial<UserColumnFilters> {
  const columnFilters: Partial<UserColumnFilters> = {};
  if (!filterModel) return columnFilters;

  for (const [colId, raw] of Object.entries(filterModel)) {
    if (!raw || typeof raw !== 'object') continue;
    const model = raw as { filter?: unknown };
    if (typeof model.filter !== 'string' || !model.filter.trim()) continue;
    if (colId in USER_COLUMN_FILTER_KEY_SET) {
      columnFilters[colId as UserColumnFilterKey] = [model.filter];
    }
  }

  return columnFilters;
}

export function buildPlatformUserRequest(options: {
  filterStatus: string;
  filterRoleId: string;
  search: string;
  columnFilters: UserColumnFilters;
  export?: boolean;
}): PlatformUserListRequest {
  const request: PlatformUserListRequest = {};

  if (options.filterStatus !== 'All') {
    request.status = options.filterStatus.toUpperCase();
  }

  if (options.filterRoleId !== 'All') {
    const roleId = Number(options.filterRoleId);
    if (roleId > 0) {
      request.roleId = roleId;
    }
  }

  const query = options.search.trim();
  if (query) {
    request.search = query;
  }

  const filters = Object.fromEntries(
    USER_COLUMN_FILTER_KEYS.flatMap((key) => {
      const values = options.columnFilters[key];
      return values?.length ? [[key, values]] : [];
    }),
  );

  if (Object.keys(filters).length > 0) {
    request.filters = filters;
  }

  if (options.export) {
    request.export = true;
  }

  return request;
}

export function mapUserExportRow(user: PlatformUserRow) {
  const merchantName = user.merchantName?.trim();

  return {
    name: user.name || '—',
    email: user.email || '—',
    merchantName: merchantName && merchantName !== '—' ? merchantName : '—',
    role: user.role || '—',
    lastLogin: user.lastLoginRelative || user.lastLogin || '—',
    ...mapAuditExportFields(user),
    status: user.status || '—',
  };
}

export async function exportPlatformUsersExcel(rows: PlatformUserRow[]) {
  await exportRowsToExcel(
    rows.map(mapUserExportRow),
    USER_EXPORT_COLUMNS,
    {
      sheetName: 'Platform Users',
      fileNamePrefix: 'platform-users',
    },
  );
}
